/* eslint-disable react/require-default-props */
import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get, noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import FormattedNumber from '/component/base/FormattedNumber';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {OrderDetails} from '/constant/TestIDs';
import {
  ON_DEMAND_VALUE,
  ORDER_TYPE,
  PREMIER_VALUE,
  RETURN_VALUE,
} from '/enum/order.enum';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import {styles} from './OrderHistoryList.style';

interface OrderItem {
  id?: string | number;
  orderedOn?: string;
  orderNumber?: string;
  cv?: string;
  qv?: string;
  orderType?: string;
  price?: number | string;
  currencyCode?: string;
}

interface OrderProps {
  item?: OrderItem;
  index?: number | null;
  totalCount?: number;
  onFocus?: () => void;
  onOrderPress?: (orderId: string) => void;
}

export const Order: React.FC<OrderProps> = ({
  item: {
    id,
    orderedOn,
    orderNumber,
    cv,
    qv,
    orderType,
    price = 0,
    currencyCode,
  } = {} as OrderItem,
  index = null,
  totalCount = 0,
  onFocus = noop,
  onOrderPress = noop,
}: OrderProps) => {
  const featureFlag = useSelector(variablesFeatureFlag);
  const shouldShowNewOrderHistory = shouldReleaseFeature(
    get(featureFlag, 'ff_order_history', ''),
  );
  const updatePremierPlus = shouldReleaseFeature(featureFlag?.ff_Premier_Plus);
  const showOrderMetrics = cv || qv || orderType;

  const renderOrderType = (type: string = '', orderedDate: string = '') => {
    const normalizedType = `${type}`.trim().toLowerCase();
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type.
    const value = ORDER_TYPE[normalizedType];
    const hasPremierPlusStarted =
      updatePremierPlus &&
      getDateUtilDefaultInstance().isPremierPlusDateReached(orderedDate);

    if (value === PREMIER_VALUE) {
      return hasPremierPlusStarted ? 'Premier+' : 'Premier';
    }
    if (value === RETURN_VALUE) {
      return 'Return';
    }
    if (value === ON_DEMAND_VALUE) {
      return 'on-demand';
    }
    return '';
  };

  const listItemStyle = {
    ...styles.listItem,
    ...(index && index > 0 ? styles.listItemWithBorder : {}),
  };

  const formattedDate = getDateUtilDefaultInstance().formatDate(
    orderedOn,
    'MMMM d, yyyy',
  );
  const orderTypeText = renderOrderType(orderType, orderedOn);

  const accessibilityLabel = [
    `Ordered on ${formattedDate}. Order number #${orderNumber} Amount: ${price} ${currencyCode}.`,
    showOrderMetrics ? `CV ${parseInt(`${cv}`, 10)}` : '',
    showOrderMetrics ? `QV ${parseInt(`${qv}`, 10)}` : '',
    orderTypeText,
    typeof index === 'number' && index >= 0 && totalCount > 0
      ? `${index + 1} of ${totalCount}`
      : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Gutter key={index}>
      <ListItem
        accessible={false}
        focusable={false}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint="Double tap to open the details"
        onFocus={onFocus}
        label={
          <KeyboardFocusable
            testID={OrderDetails.orderRow}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityHint="Double tap to open the details"
            onFocus={onFocus}
            onPress={generateCallback({
              func: onOrderPress,
              params: {orderId: id},
            })}
            focusRingStyle={styles.keyboardFocusRing}>
            <View style={styles.orderRow}>
              <View style={styles.orderContent}>
                <Text
                  testID={OrderDetails.orderDate}
                  style={styles.orderDateText}>
                  {formattedDate}
                </Text>
                <Text
                  testID={OrderDetails.numberOfOrder}
                  style={styles.orderNumberText}>
                  #{orderNumber}
                </Text>
                {shouldShowNewOrderHistory && showOrderMetrics ? (
                  <View
                    testID={OrderDetails.orderMetrics}
                    style={styles.orderMetaRow}>
                    <View style={styles.orderMetaItemContainer}>
                      <Text
                        testID={OrderDetails.cv}
                        variant="caption"
                        emphasis="low"
                        style={styles.orderMetaItemText}>
                        CV {parseInt(`${cv}`, 10)}
                      </Text>
                    </View>
                    <View style={styles.orderMetaItemContainer}>
                      <Text
                        testID={OrderDetails.qv}
                        variant="caption"
                        emphasis="low"
                        style={styles.orderMetaItemText}>
                        QV {parseInt(`${qv}`, 10)}
                      </Text>
                    </View>
                    <View style={styles.orderMetaItemContainer}>
                      <Text
                        testID={OrderDetails.orderType}
                        variant="caption"
                        emphasis="low"
                        style={styles.orderMetaItemText}>
                        {renderOrderType(orderType, orderedOn)}
                      </Text>
                    </View>
                  </View>
                ) : null}
              </View>
              <View style={styles.orderHistoryList}>
                <FormattedNumber
                  testID={OrderDetails.orderTotal}
                  value={price}
                  currencyCode={currencyCode}
                  decimalPlaces={2}
                />
                {shouldShowNewOrderHistory ? (
                  <Icon name="chevron-right" style={styles.arrowIcon} />
                ) : null}
              </View>
            </View>
          </KeyboardFocusable>
        }
        onPress={generateCallback({func: onOrderPress, params: {orderId: id}})}
        style={{base: listItemStyle}}
      />
    </Gutter>
  );
};
